import { phoneParseNumber, phoneGetCountryCallingCode, Country } from '@estee/elc-inputs';
import { IAddressSchemaProperties, ICountryCallingCodeConfig } from '../api/JsonSchemaApi';
import { ICountryLabel, COUNTRY_LABELS } from '../constants/CountryLabels';
import { FIELD_TYPE } from '../constants/Constants';

export function getCountryAbbr(
  country: number,
): string {
  const countryId = country && Number(country);
  const countryObj = COUNTRY_LABELS.find(
    (c: ICountryLabel) => c.value === countryId,
  );
  return (countryObj && countryObj.abbr.toLowerCase()) || '';
}

// Enable phone formatting/masking just for some of the countries due to backward compatibility
// issues where we cannot determine the original phone's country (phone vs address country) so
// we cannot correctly format them. ie BK FR

// need to look into why this is needed, not happy with the original formatter?
const customFormatting: Country[] = ['US', 'CA'];

export function formatPhone(number: string, country: number, asNational = false): string {
  // parsePhoneNumberFromString(`+${prefix}${number}`); this can help on preventing
  // the formatter adding additional prefix country code when phone is not standard,
  // is too short or intentional bad phone number.
  // If phone number is ok, the prefix issue will not happen.

  // Since we will end-up saving national OR international phone numbers is better to use
  // an already working solution for correct phone numbers

  const countryId = Number(country);
  const countryCode = getCountryAbbr(countryId).toUpperCase() as Country;
  if (countryCode) {
    // workaround for the international phone number without a plus sign parsed invalid
    // https://github.com/catamphetamine/libphonenumber-js/issues/376
    let phoneNumber = phoneParseNumber(number, countryCode);
    const isPhoneAndCountryFilled = phoneNumber && country;
    const numberNotStartsWithPlusSign = number[0] !== '+';
    if (isPhoneAndCountryFilled && (!phoneNumber.isValid() || numberNotStartsWithPlusSign)) {
      // try to parse an invalid phone number by adding missing + sign
      if (number.indexOf(phoneGetCountryCallingCode(countryCode) as string) === 0) {
        phoneNumber = phoneParseNumber(`+ ${number}`, countryCode);
      }
    }
    if (phoneNumber) {
      const { countryCallingCode } = phoneNumber;
      return customFormatting.includes(countryCode)
        ? `+${countryCallingCode} ${phoneNumber.formatNational()}`
        : asNational ? phoneNumber.formatNational() : phoneNumber.formatInternational();
    }
  }
  return number;
}

// return a phone number in E.164 format
// ie: input: 0728271122, output: +32728271122
export function formatPhoneIntoE164(phone = '', country = 0): string {
  const countryId = Number(country);
  const countryCode = getCountryAbbr(countryId).toUpperCase() as Country;
  const notInE164Format = phone?.[0] !== '+';
  const isPhoneAndCountryFilled = phone && country;
  if (isPhoneAndCountryFilled && notInE164Format) {
    let phoneNumber = phoneParseNumber(phone, countryCode);
    if (phoneNumber && !phoneNumber.isValid()) {
      // try to parse an invalid phone number by adding missing + sign
      if (phone.indexOf(phoneGetCountryCallingCode(countryCode) as string) === 0) {
        phoneNumber = phoneParseNumber(`+${phone}`, countryCode);
      }
    }

    return phoneNumber?.number || phone;
  }
  return phone;
}

export const addPlusSignForE164Pattern = (phone: string): string => phone?.replace(/(\^)(?!\\\+)(?!\+)/g, '$1\\+?');

// TODO this should be configurable (this is limiting shipping to europe region)
// thinking we should do this within site config (ie, phone_configs)
export function getPhoneRegions(regionId: number, isBilling: boolean): string[] | null {
  if (regionId === 5 && !isBilling) {
    return ['europe'];
  }
  return null;
}

export function getCountryCodesFromSchema(
  addressFormSchema?: IAddressSchemaProperties,
): ICountryCallingCodeConfig[] {
  const countryCodes =
    addressFormSchema?.[FIELD_TYPE.PHONE]?.additionalProperties?.countryCodes ||
    addressFormSchema?.[FIELD_TYPE.SECOND_PHONE]?.additionalProperties?.countryCodes;

  return countryCodes || [];
}

export function phoneHasCountryCallingCode(phone: string, countryCallingCode: string): boolean {
  const phoneDigits = phone.replace(/\D/g, '');
  return phoneDigits.startsWith(countryCallingCode);
}

// get phone country code config by phone number
// return the config for which the phone number start with the corresponding country code
export function getMatchingCountryCodeConfig(
  countryCodes: ICountryCallingCodeConfig[], mobileNumber: string,
): ICountryCallingCodeConfig | null {
  const phoneDigits = mobileNumber.replace(/\D/g, '');
  const countryCallingCodeItem = countryCodes.find(
    item => phoneHasCountryCallingCode(phoneDigits, item.countryCallingCode),
  );
  if (countryCallingCodeItem) {
    return countryCallingCodeItem;
  }
  return null;
}

export const trimCountryCode = (mobileNumber: string, countryCallingCode: string): string => {
  const countryCodeLength = countryCallingCode.toString().length;
  const numberWithoutCountryCode = mobileNumber.replace(/\D/g, '');
  
  if (numberWithoutCountryCode.startsWith(countryCallingCode)) {
    return numberWithoutCountryCode.substring(countryCodeLength);
  }
  
  return numberWithoutCountryCode;
};

/**
 * Check if the input is dynamic input which can config the translation label 
 * @param labelKey the schema additionalProperties labelKey value
 * @param fieldType
 * @returns 
 */
export const isDynamicFormInput = (labelKey: string, fieldType: FIELD_TYPE): boolean => {
  const normalInputs = [
    FIELD_TYPE.PHONE,
    FIELD_TYPE.SECOND_PHONE,
    FIELD_TYPE.ZIPCODE,
    FIELD_TYPE.CITY,
    FIELD_TYPE.STATE,
    FIELD_TYPE.FIFTH_LINE_ADDRESS,
    FIELD_TYPE.SECOND_LINE_ADDRESS,
    FIELD_TYPE.THIRD_LINE_ADDRESS,
    FIELD_TYPE.FOURTH_LINE_ADDRESS,
    FIELD_TYPE.FIFTH_LINE_ADDRESS,
    FIELD_TYPE.COLONY,
    FIELD_TYPE.MUNICIPALITY,
    FIELD_TYPE.DELIVERY_INSTRUCTIONS,
    FIELD_TYPE.COMPANY_NAME,
  ];
  return !!labelKey && !normalInputs.includes(fieldType);
};
